import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  RemoveFormatting,
  RotateCcw,
  RotateCw,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react';

interface RichTextEditorProps {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  id?: string;
  className?: string;
}

export function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'İçeriğinizi buraya yazın veya biçimlendirin...',
  minHeight = 'min-h-[260px]',
  id,
  className = '',
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isCodeView, setIsCodeView] = useState(false);
  const [htmlSource, setHtmlSource] = useState(value ?? '');
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    unorderedList: false,
    orderedList: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    blockquote: false,
    heading: 'p',
  });

  // Link ve Resim modal durumlari
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpenNewTab, setLinkOpenNewTab] = useState(true);
  const savedSelectionRef = useRef<Range | null>(null);

  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');

  // Disaridan value degistiginde icerigi guncelle
  useEffect(() => {
    const currentVal = value ?? '';
    setHtmlSource(currentVal);

    if (editorRef.current && !isCodeView) {
      if (editorRef.current.innerHTML !== currentVal) {
        editorRef.current.innerHTML = currentVal;
      }
    }
  }, [value, isCodeView]);

  // Aktif formatlari guncelle (bold, italic vb.)
  const updateActiveFormats = useCallback(() => {
    if (isCodeView || !document.getSelection()?.anchorNode) return;

    try {
      const bold = document.queryCommandState('bold');
      const italic = document.queryCommandState('italic');
      const underline = document.queryCommandState('underline');
      const strikeThrough = document.queryCommandState('strikeThrough');
      const unorderedList = document.queryCommandState('insertUnorderedList');
      const orderedList = document.queryCommandState('insertOrderedList');
      const justifyLeft = document.queryCommandState('justifyLeft');
      const justifyCenter = document.queryCommandState('justifyCenter');
      const justifyRight = document.queryCommandState('justifyRight');

      let heading = 'p';
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let parent: Node | null = selection.getRangeAt(0).commonAncestorContainer;
        while (parent && parent !== editorRef.current) {
          if (parent.nodeName === 'H1') {
            heading = 'h1';
            break;
          } else if (parent.nodeName === 'H2') {
            heading = 'h2';
            break;
          } else if (parent.nodeName === 'H3') {
            heading = 'h3';
            break;
          } else if (parent.nodeName === 'BLOCKQUOTE') {
            heading = 'blockquote';
            break;
          }
          parent = parent.parentNode;
        }
      }

      setActiveFormats({
        bold,
        italic,
        underline,
        strikeThrough,
        unorderedList,
        orderedList,
        justifyLeft,
        justifyCenter,
        justifyRight,
        blockquote: heading === 'blockquote',
        heading,
      });
    } catch {
      /* Komut durumu alinamadiysa sorun degil */
    }
  }, [isCodeView]);

  // Editor degisikligini bildir
  const handleInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    setHtmlSource(html);
    onChange(html);
    updateActiveFormats();
  };

  // Komut calistir
  const execCmd = (command: string, val: string | undefined = undefined) => {
    if (isCodeView) return;
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, val);
    handleInput();
  };

  // Baslik / Paragraf degistir
  const handleHeadingChange = (format: string) => {
    if (isCodeView) return;
    if (editorRef.current) {
      editorRef.current.focus();
    }

    if (format === 'p') {
      execCmd('formatBlock', '<p>');
    } else if (format === 'h1' || format === 'h2' || format === 'h3') {
      execCmd('formatBlock', `<${format}>`);
    } else if (format === 'blockquote') {
      execCmd('formatBlock', '<blockquote>');
    }
  };

  // Kod / Gorsel gorunumu gecisi
  const toggleViewMode = () => {
    if (isCodeView) {
      // Kod modundan gorsel moda gecis
      setIsCodeView(false);
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = htmlSource;
          editorRef.current.focus();
          updateActiveFormats();
        }
      }, 0);
    } else {
      // Gorsel moddan kod moduna gecis
      if (editorRef.current) {
        setHtmlSource(editorRef.current.innerHTML);
      }
      setIsCodeView(true);
    }
  };

  const handleSourceChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setHtmlSource(val);
    onChange(val);
  };

  // Secimi kaydet (dialog acilmadan once)
  const saveCurrentSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // Secimi geri yukle
  const restoreSelection = () => {
    if (savedSelectionRef.current && window.getSelection) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
      }
    }
  };

  // Baglanti Ekle Dialogunu Ac
  const openLinkDialog = () => {
    saveCurrentSelection();
    setLinkUrl('');
    setLinkOpenNewTab(true);
    setShowLinkDialog(true);
  };

  const insertLink = () => {
    setShowLinkDialog(false);
    restoreSelection();
    if (!linkUrl.trim()) return;

    let targetUrl = linkUrl.trim();
    if (!/^https?:\/\//i.test(targetUrl) && !targetUrl.startsWith('/') && !targetUrl.startsWith('#')) {
      targetUrl = `https://${targetUrl}`;
    }

    execCmd('createLink', targetUrl);

    // Yeni sekme acma ozelligi
    if (linkOpenNewTab && editorRef.current) {
      const links = editorRef.current.getElementsByTagName('a');
      for (let i = 0; i < links.length; i++) {
        if (links[i].getAttribute('href') === targetUrl) {
          links[i].setAttribute('target', '_blank');
          links[i].setAttribute('rel', 'noopener noreferrer');
        }
      }
      handleInput();
    }
  };

  // Resim Ekle Dialogunu Ac
  const openImageDialog = () => {
    saveCurrentSelection();
    setImageUrl('');
    setImageAlt('');
    setShowImageDialog(true);
  };

  const insertImage = () => {
    setShowImageDialog(false);
    restoreSelection();
    if (!imageUrl.trim()) return;

    const imgHtml = `<img src="${imageUrl.trim()}" alt="${imageAlt.trim()}" class="max-w-full rounded-lg my-2" />`;
    execCmd('insertHTML', imgHtml);
  };

  // Kelime ve karakter sayisi
  const textContent = isCodeView
    ? htmlSource.replace(/<[^>]*>/g, '')
    : editorRef.current?.innerText || '';
  const wordCount = textContent.trim() ? textContent.trim().split(/\s+/).length : 0;
  const charCount = textContent.length;

  return (
    <div className={`relative flex flex-col rounded-xl border border-ink-200 bg-white shadow-xs focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100 transition ${className}`}>
      {/* Arac Cubugu (Toolbar) */}
      <div className="flex flex-wrap items-center gap-1 border-b border-ink-100 bg-ink-50/70 p-2 rounded-t-xl select-none">
        {/* Paragraf ve Basliklar */}
        <div className="flex items-center gap-1 mr-1">
          <button
            type="button"
            disabled={isCodeView}
            onClick={() => handleHeadingChange('p')}
            title="Normal Paragraf"
            className={`flex h-8 items-center gap-1 rounded px-2 text-xs font-medium transition cursor-pointer ${
              activeFormats.heading === 'p' && !isCodeView
                ? 'bg-brand-50 text-brand-700 font-semibold border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Type className="h-3.5 w-3.5" />
            <span>Normal</span>
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => handleHeadingChange('h1')}
            title="Başlık 1 (H1)"
            className={`flex h-8 items-center gap-1 rounded px-2 text-xs font-medium transition cursor-pointer ${
              activeFormats.heading === 'h1' && !isCodeView
                ? 'bg-brand-50 text-brand-700 font-semibold border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Heading1 className="h-3.5 w-3.5" />
            <span>H1</span>
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => handleHeadingChange('h2')}
            title="Başlık 2 (H2)"
            className={`flex h-8 items-center gap-1 rounded px-2 text-xs font-medium transition cursor-pointer ${
              activeFormats.heading === 'h2' && !isCodeView
                ? 'bg-brand-50 text-brand-700 font-semibold border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Heading2 className="h-3.5 w-3.5" />
            <span>H2</span>
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => handleHeadingChange('h3')}
            title="Başlık 3 (H3)"
            className={`flex h-8 items-center gap-1 rounded px-2 text-xs font-medium transition cursor-pointer ${
              activeFormats.heading === 'h3' && !isCodeView
                ? 'bg-brand-50 text-brand-700 font-semibold border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Heading3 className="h-3.5 w-3.5" />
            <span>H3</span>
          </button>
        </div>

        <div className="h-4 w-px bg-ink-200 mx-1" />

        {/* Metin Formatlama: Kalin, Italik, Alti Cizili, Ustu Cizili */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('bold')}
            title="Kalın (Ctrl+B)"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.bold && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Bold className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('italic')}
            title="İtalik (Ctrl+I)"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.italic && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Italic className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('underline')}
            title="Altı Çizili (Ctrl+U)"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.underline && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Underline className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('strikeThrough')}
            title="Üstü Çizili"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.strikeThrough && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Strikethrough className="h-4 w-4" />
          </button>
        </div>

        <div className="h-4 w-px bg-ink-200 mx-1" />

        {/* Listeler: Madde Isaretli, Numarali */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('insertUnorderedList')}
            title="Madde İşaretli Liste"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.unorderedList && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <List className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('insertOrderedList')}
            title="Numaralı Liste"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.orderedList && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <ListOrdered className="h-4 w-4" />
          </button>
        </div>

        <div className="h-4 w-px bg-ink-200 mx-1" />

        {/* Hizalama: Sol, Orta, Sag */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('justifyLeft')}
            title="Sola Hizala"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.justifyLeft && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <AlignLeft className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('justifyCenter')}
            title="Ortala"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.justifyCenter && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <AlignCenter className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('justifyRight')}
            title="Sağa Hizala"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.justifyRight && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <AlignRight className="h-4 w-4" />
          </button>
        </div>

        <div className="h-4 w-px bg-ink-200 mx-1" />

        {/* Baglanti, Resim, Alinti, Cizgi */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={isCodeView}
            onClick={openLinkDialog}
            title="Bağlantı Ekle"
            className="flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 transition disabled:opacity-40 cursor-pointer"
          >
            <Link2 className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={openImageDialog}
            title="Görsel Ekle"
            className="flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 transition disabled:opacity-40 cursor-pointer"
          >
            <ImageIcon className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => handleHeadingChange('blockquote')}
            title="Alıntı Ekle"
            className={`flex h-8 w-8 items-center justify-center rounded transition cursor-pointer ${
              activeFormats.blockquote && !isCodeView
                ? 'bg-brand-50 text-brand-700 border border-brand-200'
                : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 disabled:opacity-40'
            }`}
          >
            <Quote className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('insertHorizontalRule')}
            title="Yatay Çizgi Ekle"
            className="flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 transition disabled:opacity-40 cursor-pointer"
          >
            <Minus className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('removeFormat')}
            title="Biçimlendirmeyi Temizle"
            className="flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 transition disabled:opacity-40 cursor-pointer"
          >
            <RemoveFormatting className="h-4 w-4" />
          </button>
        </div>

        <div className="h-4 w-px bg-ink-200 mx-1" />

        {/* Geri / Ileri Al */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('undo')}
            title="Geri Al (Ctrl+Z)"
            className="flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 transition disabled:opacity-40 cursor-pointer"
          >
            <RotateCcw className="h-4 w-4" />
          </button>

          <button
            type="button"
            disabled={isCodeView}
            onClick={() => execCmd('redo')}
            title="İleri Al (Ctrl+Y)"
            className="flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-200/70 hover:text-ink-900 transition disabled:opacity-40 cursor-pointer"
          >
            <RotateCw className="h-4 w-4" />
          </button>
        </div>

        {/* HTML / Kod Modu Gecis Butonu */}
        <div className="ml-auto flex items-center">
          <button
            type="button"
            onClick={toggleViewMode}
            title={isCodeView ? 'Görsel Editöre Dön' : 'HTML Kaynak Kodunu Düzenle'}
            className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition cursor-pointer ${
              isCodeView
                ? 'bg-ink-900 text-white shadow-xs'
                : 'bg-ink-100 text-ink-800 hover:bg-ink-200'
            }`}
          >
            {isCodeView ? <Eye className="h-3.5 w-3.5" /> : <Code className="h-3.5 w-3.5" />}
            <span>{isCodeView ? 'Görsel Mod' : 'HTML Kaynak'}</span>
          </button>
        </div>
      </div>

      {/* Editor Govdesi: Gorsel (WYSIWYG) veya HTML Kaynak Kodu */}
      <div className="relative flex-1">
        {isCodeView ? (
          <div className="relative">
            <div className="bg-ink-900/5 px-3 py-1.5 text-[11px] font-mono text-ink-600 border-b border-ink-100 flex items-center justify-between">
              <span>HTML Kaynak Modu</span>
              <span className="text-[10px] text-ink-400">Doğrudan HTML etiketleri yazabilirsiniz</span>
            </div>
            <textarea
              value={htmlSource}
              onChange={handleSourceChange}
              className={`w-full p-4 font-mono text-xs text-ink-800 bg-ink-50/30 focus:outline-hidden focus:bg-white resize-y ${minHeight}`}
              placeholder="<p>HTML kodunuzu buraya yazın...</p>"
              spellCheck={false}
            />
          </div>
        ) : (
          <div
            id={id}
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            onKeyUp={updateActiveFormats}
            onMouseUp={updateActiveFormats}
            onSelect={updateActiveFormats}
            className={`prose max-w-none p-4 text-sm text-ink-900 focus:outline-hidden ${minHeight} [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:mb-2 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-0.5 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-500 [&_blockquote]:bg-brand-50/40 [&_blockquote]:pl-3 [&_blockquote]:py-1 [&_blockquote]:italic [&_blockquote]:my-2 [&_blockquote]:rounded-r [&_a]:text-brand-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2 [&_hr]:my-4 [&_hr]:border-ink-200`}
            data-placeholder={placeholder}
          />
        )}
      </div>

      {/* Alt Durum Cubugu */}
      <div className="flex items-center justify-between border-t border-ink-100 bg-ink-50/50 px-3 py-1.5 text-[11px] text-ink-500 rounded-b-xl">
        <div className="flex items-center gap-3">
          <span>{wordCount} kelime</span>
          <span>•</span>
          <span>{charCount} karakter</span>
        </div>
        <div>
          {isCodeView ? (
            <span className="font-mono text-[10px] text-ink-600">HTML Kodu</span>
          ) : (
            <span>Görsel Editör</span>
          )}
        </div>
      </div>

      {/* Baglanti Ekle Modal / Dialog */}
      {showLinkDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="card w-full max-w-md p-5 shadow-xl bg-white animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-sm font-semibold text-ink-900 mb-3">Bağlantı (Link) Ekle</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ink-700 mb-1">Web Adresi (URL)</label>
                <input
                  type="text"
                  className="field"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      insertLink();
                    }
                  }}
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-xs text-ink-700">
                <input
                  type="checkbox"
                  checked={linkOpenNewTab}
                  onChange={(e) => setLinkOpenNewTab(e.target.checked)}
                  className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                <span>Yeni sekmede aç (target="_blank")</span>
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setShowLinkDialog(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={insertLink}
                  disabled={!linkUrl.trim()}
                >
                  Bağlantıyı Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Gorsel Ekle Modal / Dialog */}
      {showImageDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="card w-full max-w-md p-5 shadow-xl bg-white animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-sm font-semibold text-ink-900 mb-3">Görsel Ekle</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ink-700 mb-1">Görsel URL'si</label>
                <input
                  type="text"
                  className="field"
                  placeholder="https://example.com/resim.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-700 mb-1">Alternatif Metin (Alt Text)</label>
                <input
                  type="text"
                  className="field"
                  placeholder="Görsel açıklaması"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      insertImage();
                    }
                  }}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setShowImageDialog(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={insertImage}
                  disabled={!imageUrl.trim()}
                >
                  Görseli Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
